import {init as initAmplitude, logEvent} from "@amplitude/analytics-browser";

const apiKey = "82545d9e16144f93114bd0df0651476b";
const isAmplitudeEnabled = import.meta.env.VITE_ENABLE_AMPLITUDE === "true";

export const initializeAmplitude = (userId = null) => {
  if (!isAmplitudeEnabled) return;

  initAmplitude(apiKey, {
    defaultTracking: true,
    serverZone: "US",
    userId: userId,
  });
};

// Meta Pixel Helper Functions
const fbq = (...args) => {
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq(...args);
  }
};

/**
 * Track event to both Amplitude and Meta Pixel
 * @param {string} eventName - Event name
 * @param {object} params - Event parameters
 */
const trackToBothPlatforms = (eventName, params = {}) => {
  if (isAmplitudeEnabled) {
    logEvent(eventName, params);
  }

  fbq("trackCustom", eventName, params);
};

// Game Events
export const trackGameStart = (gameName, provider) => {
  const params = {
    gameName: gameName,
    provider: provider,
  };

  trackToBothPlatforms("game_start", params);
};

// User Events
export const trackUserRegistration = userId => {
  const params = {
    userId: userId,
  };

  if (isAmplitudeEnabled) {
    logEvent("user_registration", params);
  }

  fbq("track", "CompleteRegistration", params);
};

export const trackUserLogin = userId => {
  const params = {
    userId: userId,
  };

  if (isAmplitudeEnabled) {
    logEvent("user_login", params);
  }

  fbq("trackCustom", "UserLogin", params);
};

// Purchase/Transaction Events
export const trackRaffleTicketPurchase = (raffleId, numberOfTickets) => {
  const params = {
    raffleId: raffleId,
    numberOfTickets: numberOfTickets,
  };

  trackToBothPlatforms("raffle_ticket_purchase", params);
};

export const trackPurchase = (value, currency = "VES", contentName = "") => {
  const params = {
    value: value,
    currency: currency,
    content_name: contentName,
  };

  if (isAmplitudeEnabled) {
    logEvent("purchase", params);
  }

  fbq("track", "Purchase", params);
};

// Deposit/Withdrawal Events
export const trackDepositButtonClick = () => {
  trackToBothPlatforms("deposit_button_clicked", null);
};

export const trackDepositButtonMenuClick = () => {
  trackToBothPlatforms("deposit_button_menu_clicked", null);
};

export const trackDeposit = (amount, currency = "VES", method = "") => {
  const params = {
    amount: amount,
    currency: currency,
    payment_method: method,
  };

  if (isAmplitudeEnabled) {
    logEvent("deposit_completed", params);
  }

  fbq("trackCustom", "DepositCompleted", params);
};

export const trackWithdrawal = (amount, currency = "VES", method = "") => {
  const params = {
    amount: amount,
    currency: currency,
    payment_method: method,
  };

  trackToBothPlatforms("withdrawal_completed", params);
};

// Add to Cart / Wishlist
export const trackAddToCart = (contentName, value, currency = "VES") => {
  const params = {
    content_name: contentName,
    value: value,
    currency: currency,
  };

  if (isAmplitudeEnabled) {
    logEvent("add_to_cart", params);
  }

  fbq("track", "AddToCart", params);
};

export const trackAddToWishlist = contentName => {
  const params = {
    content_name: contentName,
  };

  if (isAmplitudeEnabled) {
    logEvent("add_to_wishlist", params);
  }

  fbq("track", "AddToWishlist", params);
};

// Search Event
export const trackSearch = (searchQuery, contentCategory = "") => {
  const params = {
    search_string: searchQuery,
    content_category: contentCategory,
  };

  if (isAmplitudeEnabled) {
    logEvent("search", params);
  }

  fbq("track", "Search", params);
};

// View Content Event
export const trackViewContent = (contentName, contentType = "", contentId = "") => {
  const params = {
    content_name: contentName,
    content_type: contentType,
    content_ids: contentId ? [contentId] : [],
  };

  if (isAmplitudeEnabled) {
    logEvent("view_content", params);
  }

  fbq("track", "ViewContent", params);
};

// Lead Event
export const trackLead = (contentName = "") => {
  const params = {
    content_name: contentName,
  };

  if (isAmplitudeEnabled) {
    logEvent("lead_generated", params);
  }

  fbq("track", "Lead", params);
};
